// "Continue with Google" for the patient portal. The browser goes to Google
// with a PKCE challenge, a state and a nonce; Google sends it back to
// /auth/google/callback (GoogleCallbackPage), which hands the code to the
// backend together with the verifier and nonce kept here.
//
// sessionStorage, not localStorage: the values only have to survive the round
// trip to Google in this one tab, and a second tab must not pick them up.
import { getGoogleConfigApi, type GoogleAuthResponse } from "@/portals/patient/api/patientAuthApi";
import { usePatientAuthStore, toSession } from "@/stores/patientAuthStore";
import { savePendingClaimGuidance } from "./claimGuidance";

const GOOGLE_FLOW_KEY = "medsynciq_google_flow";
const GOOGLE_AUTHORIZE_URL = "https://accounts.google.com/o/oauth2/v2/auth";

/** Must match an Authorized redirect URI in the Google Cloud console exactly. */
export const googleRedirectUri = () => `${window.location.origin}/auth/google/callback`;

interface GoogleFlow {
  state: string;
  nonce: string;
  codeVerifier: string;
}

function randomString(bytes = 32): string {
  const values = crypto.getRandomValues(new Uint8Array(bytes));
  return base64Url(values);
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function codeChallengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/**
 * Leaves the app for Google's account chooser. Resolves with an error message
 * only when it could not start (the page has navigated away otherwise).
 */
export async function startGoogleSignIn(): Promise<string | null> {
  const { data, error } = await getGoogleConfigApi();
  if (!data) return error ?? "Unable to reach the server. Please try again.";
  if (!data.clientId) return "Google sign in is not available right now.";

  const flow: GoogleFlow = { state: randomString(), nonce: randomString(), codeVerifier: randomString(48) };
  sessionStorage.setItem(GOOGLE_FLOW_KEY, JSON.stringify(flow));

  const params = new URLSearchParams({
    client_id: data.clientId,
    redirect_uri: googleRedirectUri(),
    response_type: "code",
    scope: "openid email profile",
    state: flow.state,
    nonce: flow.nonce,
    code_challenge: await codeChallengeFor(flow.codeVerifier),
    code_challenge_method: "S256",
    prompt: "select_account",
  });
  window.location.assign(`${GOOGLE_AUTHORIZE_URL}?${params.toString()}`);
  return null;
}

/**
 * Returns the saved verifier and nonce only when `state` matches the one this
 * tab sent to Google, so a callback URL started by someone else is refused.
 * Single use: cleared either way.
 */
export function consumeGoogleFlow(state: string | null): Omit<GoogleFlow, "state"> | null {
  const raw = sessionStorage.getItem(GOOGLE_FLOW_KEY);
  sessionStorage.removeItem(GOOGLE_FLOW_KEY);
  if (!raw || !state) return null;
  try {
    const flow = JSON.parse(raw) as GoogleFlow;
    return flow.state === state ? { nonce: flow.nonce, codeVerifier: flow.codeVerifier } : null;
  } catch {
    return null;
  }
}

/** Puts a "signed_in" Google response into the patient session. */
export function applyGoogleSession(data: GoogleAuthResponse) {
  if (!data.accessToken || !data.patient) return;
  if (data.wasClaim) {
    // Same one-time card a claim verified by OTP shows on the first Dashboard visit.
    savePendingClaimGuidance({ email: data.patient.email, creatorName: data.creatorName ?? null });
  }
  usePatientAuthStore.getState().setSession(toSession({ accessToken: data.accessToken, patient: data.patient }));
}
